import React from "react";
import {
  Typography,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  alpha,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const FAQ = () => {
  const theme = useTheme();

  const faqs = [
    {
      question: "How do I log in?",
      answer:
        "To log in, enter your username and password on the login page. If you have forgotten your password, please contact the administrator.",
    },
    {
      question: "How do I create a new loading log?",
      answer:
        "Go to 'Loading Logs' and click on 'Create New'. Fill in the required information and save the log.",
    },
    {
      question: "Can I edit a saved log?",
      answer:
        "Yes, go to your list of loading logs, click on the one you want to edit, make your changes, and save.",
    },
    {
      question: "How does my data sync with the cloud?",
      answer:
        "When you are online, data is automatically saved and retrieved from the cloud service. If you are offline, changes are stored locally until your device reconnects.",
    },
    {
      question: "What should I do if I lose my internet connection?",
      answer:
        "If you lose your connection, you can continue working. Your changes will be saved locally and synchronized once you are back online.",
    },
    {
      question: "Who has access to my data?",
      answer:
        "Only authorized users with the correct roles can access certain data. Administrators control user permissions.",
    },
    {
      question: "Can I print a checklist?",
      answer:
        "Yes, go to the 'Checklists' section, select the checklist you want, and click 'View & Print'.",
    },
    {
      question: "How do I change my password?",
      answer:
        "Currently, password changes must be handled by an administrator. Please contact support for assistance.",
    },
    {
      question: "How often is my data backed up?",
      answer:
        "Your data is stored securely in the cloud in real-time. Any updates you make are automatically saved.",
    },
    {
      question: "Can I access my data from another device?",
      answer:
        "Yes, as long as you log in with your credentials on a device connected to the internet, your data will be available.",
    },
  ];

  // Samma stil som i BaseSettings för att skapa en röd tråd i appen
  const accordionStyle = {
    elevation: 0,
    disableGutters: true,
    sx: {
      border: "1px solid",
      borderColor: "divider",
      mb: 1, // Lite mellanrum mellan frågorna
      borderRadius: "8px !important", // Fristående rundade frågor ser bättre ut i FAQ
      "&:before": { display: "none" },
      transition: "all 0.2s",
      "&:hover": {
        borderColor: "primary.main",
        bgcolor: alpha(theme.palette.primary.main, 0.01),
      },
      "&.Mui-expanded": {
        my: 1,
        boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.05)}`,
      },
    },
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <HelpOutlineIcon color="primary" sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            FAQ
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Frequently Asked Questions & Support
          </Typography>
        </Box>
      </Box>

      {faqs.map((faq, index) => (
        <Accordion key={index} {...accordionStyle}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon color="primary" />}
            sx={{ px: 3, py: 1 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {faq.question}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{ lineHeight: 1.7 }}
            >
              {faq.answer}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}

      <Box
        sx={{
          mt: 6,
          p: 3,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderRadius: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Didn't find your question? No worries! We're here to help.
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Contact the bridge or your administrator for further assistance.
        </Typography>
      </Box>
    </Container>
  );
};

export default FAQ;
